import React, { useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Tag, Descriptions, Collapse } from "antd";
// INTERFACES
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { directionDisplayName } from "../../../flatbuffers/types/enumDisplayUtils";
import {
  contentTypeDisplayName,
  messageTypeDisplayName,
  msgInnerDisplayName,
} from "../../../flatbuffers/types/messageDisplayUtils";
// UTILS
import { getServiceModeDisplay } from "../../../utils/serviceModeDisplay";
import getFaultDisplayName from "../../../utils/faultDisplay";
import { extractSuffix } from "../../../utils/stateMapHelpers";
import { uuidToString } from "../../../utils/uuid";
import { LiftType } from "../../../interfaces/Lift.interface";

export default function ReplayFilesPrettyJson({
  socketMessage,
  liftType,
}: {
  socketMessage: SocketMessageInterface;
  liftType: LiftType;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Panel } = Collapse;

  const serviceModeInfo = useMemo(
    () =>
      getServiceModeDisplay(
        socketMessage?.serviceMode?.messageType ?? null,
        socketMessage?.serviceMode?.message?.message?.message ?? null,
      ),
    [socketMessage?.serviceMode],
  );

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div className="generalStyles__flex">
        <div className="generalStyles__width100">
          <Descriptions bordered size="small">
            <Descriptions.Item label="Bank ID" span={24}>
              {uuidToString(socketMessage?.bankId) || "--"}
            </Descriptions.Item>
            <Descriptions.Item label="Position" span={24}>
              <Tag
                color="geekblue"
                className="generalStyles__width150px generalStyles__textCenter "
              >
                {socketMessage?.position !== null &&
                socketMessage?.position !== undefined &&
                socketMessage.position >= 0
                  ? socketMessage.position
                  : "N/A"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Direction" span={24}>
              <Tag color="purple" className="generalStyles__width150px generalStyles__textCenter">
                {directionDisplayName(socketMessage.direction)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Message" span={24}>
              <Tag
                color={socketMessage?.msg?.content?.message ? "gold" : ""}
                className="generalStyles__width150px generalStyles__textCenter "
              >
                {msgInnerDisplayName({
                  liftType,
                  contentType: socketMessage?.msg?.contentType,
                  messageType: socketMessage?.msg?.messageType,
                  content: socketMessage?.msg?.content,
                })}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Content Type" span={24}>
              <Tag
                color={socketMessage?.msg?.contentType ? "orange" : ""}
                className="generalStyles__width150px generalStyles__textCenter "
              >
                {contentTypeDisplayName(socketMessage?.msg?.contentType)}{" "}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Message Type" span={24}>
              <Tag
                color={socketMessage?.msg?.messageType ? "cyan" : ""}
                className="generalStyles__width150px generalStyles__textCenter "
              >
                {messageTypeDisplayName(socketMessage?.msg?.messageType)}{" "}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Message Active" span={24}>
              {socketMessage?.msg?.active ? (
                <Tag color="green" className="generalStyles__width150px generalStyles__textCenter">
                  True
                </Tag>
              ) : (
                <Tag color="red" className="generalStyles__width150px generalStyles__textCenter">
                  False
                </Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
        </div>
        <div className="generalStyles__width100">
          <Descriptions bordered size="small">
            <Descriptions.Item label="Lift ID" span={24}>
              {uuidToString(socketMessage?.liftId) || "--"}
            </Descriptions.Item>
            <Descriptions.Item label="Faults" span={24}>
              <Tag
                color={socketMessage?.faults ? "red" : "green"}
                className="generalStyles__width150px generalStyles__textCenter"
              >
                {socketMessage?.faults ? "Faults" : "No faults"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Service Mode" span={24}>
              <Tag
                color={serviceModeInfo?.color ?? "cyan"}
                className="generalStyles__width150px generalStyles__textCenter"
              >
                {serviceModeInfo?.displayName ?? "--"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Front door" span={24}>
              <Tag
                color={socketMessage?.doorStatus?.frontOpen ? "green" : "red"}
                className="generalStyles__width150px generalStyles__textCenter"
              >
                {socketMessage?.doorStatus?.frontOpen ? "Open / True" : "Close / False"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Rear door" span={24}>
              <Tag
                color={socketMessage?.doorStatus?.rearOpen ? "green" : "red"}
                className="generalStyles__width150px generalStyles__textCenter"
              >
                {socketMessage?.doorStatus?.rearOpen ? "Open / True" : "Close / False"}
              </Tag>{" "}
            </Descriptions.Item>
            <Descriptions.Item label="Connected" span={24}>
              <Tag
                color={socketMessage?.connected ? "green" : "red"}
                className="generalStyles__width150px generalStyles__textCenter"
              >
                {socketMessage?.connected ? "Connected" : "Disconnected"}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </div>
      </div>
      <Collapse bordered={false}>
        <Panel header="Car calls" key="1">
          <div className="generalStyles__flex">
            <div className="generalStyles__width100">
              <Descriptions bordered size="small">
                <Descriptions.Item label="Car calls front" span={24}>
                  {socketMessage?.carCalls.front.map((item: number) => (
                    <Tag color="blue">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Car calls front lock" span={24}>
                  {socketMessage?.carCalls.frontLock.map((item: number) => (
                    <Tag color="blue">{item}</Tag>
                  ))}
                </Descriptions.Item>
              </Descriptions>
            </div>
            <div className="generalStyles__width100">
              <Descriptions bordered size="small">
                <Descriptions.Item label="Car calls rear" span={24}>
                  {socketMessage?.carCalls.rear.map((item: number) => (
                    <Tag color="blue">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Car calls rear lock" span={24}>
                  {socketMessage?.carCalls.rearLock.map((item: number) => (
                    <Tag color="blue">{item}</Tag>
                  ))}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </div>
        </Panel>
        <Panel header="Hall calls" key="2">
          <div className="generalStyles__flex">
            <div className="generalStyles__width100">
              <Descriptions bordered size="small">
                <Descriptions.Item label="Hall calls up front" span={24}>
                  {socketMessage?.hallCalls.up.front.map((item: number) => (
                    <Tag color="green">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Hall calls up rear" span={24}>
                  {socketMessage?.hallCalls.up.rear.map((item: number) => (
                    <Tag color="green">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Hall calls up front lock" span={24}>
                  {socketMessage?.hallCalls.up.frontLock.map((item: number) => (
                    <Tag color="green">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Hall calls up rear lock" span={24}>
                  {socketMessage?.hallCalls.up.rearLock.map((item: number) => (
                    <Tag color="green">{item}</Tag>
                  ))}
                </Descriptions.Item>
              </Descriptions>
            </div>
            <div className="generalStyles__width100">
              <Descriptions bordered size="small">
                <Descriptions.Item label="Hall calls down front" span={24}>
                  {socketMessage?.hallCalls.down.front.map((item: number) => (
                    <Tag color="orange">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Hall calls down rear" span={24}>
                  {socketMessage?.hallCalls.down.rear.map((item: number) => (
                    <Tag color="orange">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Hall calls down front lock" span={24}>
                  {socketMessage?.hallCalls.down.frontLock.map((item: number) => (
                    <Tag color="orange">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Hall calls down rear lock" span={24}>
                  {socketMessage?.hallCalls.down.rearLock.map((item: number) => (
                    <Tag color="orange">{item}</Tag>
                  ))}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </div>
          <div className="generalStyles__flex">
            <div className="generalStyles__width100">
              <Descriptions bordered size="small">
                <Descriptions.Item label="Hall calls up assigned front" span={24}>
                  {socketMessage?.hallCalls.upAssigned.front.map((item: number) => (
                    <Tag color="cyan">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Hall calls up assigned rear" span={24}>
                  {socketMessage?.hallCalls.upAssigned.rear.map((item: number) => (
                    <Tag color="cyan">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Hall calls up assigned front lock" span={24}>
                  {socketMessage?.hallCalls.upAssigned.frontLock.map((item: number) => (
                    <Tag color="cyan">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Hall calls up assigned rear lock" span={24}>
                  {socketMessage?.hallCalls.upAssigned.rearLock.map((item: number) => (
                    <Tag color="cyan">{item}</Tag>
                  ))}
                </Descriptions.Item>
              </Descriptions>
            </div>
            <div className="generalStyles__width100">
              <Descriptions bordered size="small">
                <Descriptions.Item label="Hall calls down assigned front" span={24}>
                  {socketMessage?.hallCalls.downAssigned.front.map((item: number) => (
                    <Tag color="purple">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Hall calls down assigned rear" span={24}>
                  {socketMessage?.hallCalls.downAssigned.rear.map((item: number) => (
                    <Tag color="purple">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Hall calls down assigned front lock" span={24}>
                  {socketMessage?.hallCalls.downAssigned.frontLock.map((item: number) => (
                    <Tag color="purple">{item}</Tag>
                  ))}
                </Descriptions.Item>
                <Descriptions.Item label="Hall calls down assigned rear lock" span={24}>
                  {socketMessage?.hallCalls.downAssigned.rearLock.map((item: number) => (
                    <Tag color="purple">{item}</Tag>
                  ))}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </div>
        </Panel>
        <Panel header="Active Faults" key="3">
          {socketMessage?.activeFaults?.length > 0 ? (
            socketMessage.activeFaults.map((fault, idx) => (
              <Tag key={idx} color="red">
                {getFaultDisplayName(fault.faultType, fault.fault?.fault?.message ?? 0)}
              </Tag>
            ))
          ) : (
            <Tag color="green">No active faults</Tag>
          )}
        </Panel>
        <Panel header="Bitmask" key="4">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {socketMessage?.bitmask &&
              Object.entries(socketMessage.bitmask).map(([key, value]) => (
                <Tag key={key} color={value ? "green" : "red"}>
                  {extractSuffix(key)}
                </Tag>
              ))}
          </div>
        </Panel>
      </Collapse>
    </div>
  );
}
